import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom';
import { Col, Container, Row } from 'react-bootstrap';
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// layout 
import AppLayout from '../../../layouts/AppLayout/AppLayout';

// components  
import { NioSection, NioButton, NioIcon, NioField, NioBadge } from '../../../components';

// utils
import api from '../../../utils/api';
import { useTranslation } from '../../../i18n/I18nProvider';
import { translateServiceLabel } from '../../../i18n/translateService';

const serviceGroups = {
  'all': 'all',
  'Maison & Travaux': ['Meuble', 'Décoration', 'Cuisine', 'Salle de bain', 'Sol', 'Mur', 'Électricité', 'Plomberie', 'Chauffage', 'Menuiserie', 'Ménage', 'Nettoyage'],
  'Extérieur & Jardin': ['Jardinage', 'Extérieur', 'Aménagement'],
  'Transport': ['Déménagement', 'Transport'],
  'Sécurité': ['Sécurité', 'Serrurerie']
};

const serviceGroupLabels = {
  all: 'home.groups.all',
  'Maison & Travaux': 'home.groups.homeWorks',
  'Extérieur & Jardin': 'home.groups.outdoor',
  Transport: 'home.groups.transport',
  Sécurité: 'home.groups.security',
};

function getLoggedInUser() {
  try {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function isProviderForService(user, service) {
  if (!user?.serviceId) return false;
  const sid = (service._id && typeof service._id === 'object' && service._id.toString) ? service._id.toString() : (service._id || '');
  return user.serviceId === sid || user.serviceId === service.title;
}

const userMarkerIcon = L.divIcon({
  className: 'home-map-marker home-map-marker--user',
  html: '<span></span>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

const providerMarkerIcon = L.divIcon({
  className: 'home-map-marker home-map-marker--provider',
  html: '<span></span>',
  iconSize: [16, 16],
  iconAnchor: [8, 8],
});

function BusinessManagement() {
  const { t, language } = useTranslation();
  const [services, setServices] = useState([]);
  const [filteredServices, setFilteredServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loggedInUser, setLoggedInUser] = useState(getLoggedInUser);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const [userPosition, setUserPosition] = useState(null);

  const getServicePriority = (service) => {
    const t = String(service?.title || '').toLowerCase();
    if (t.includes('sol') || t.includes('mur')) return 0;
    if (t.includes('meuble') || t.includes('décoration') || t.includes('decoration')) return 1;
    if (t.includes('cuisine')) return 2;
    if (t.includes('salle de bain') || t.includes('salledebain') || t.includes('bain')) return 3;
    if (t.includes('plomberie') || t.includes('chauffage')) return 4;
    if (t.includes('menuiserie') || t.includes('bois')) return 5;
    if (t.includes('jardinage') || t.includes('jardin') || t.includes('extérieur') || t.includes('exterieur')) return 6;
    if (t.includes('électricité') || t.includes('electricite') || t.includes('électrique') || t.includes('electrique')) return 7;
    if (t.includes('déménagement') || t.includes('demenagement') || t.includes('transport')) return 8;
    if (t.includes('ménage') || t.includes('menage') || t.includes('nettoyage') || t.includes('netoyage')) return 9;
    if (t.includes('sécurité') || t.includes('securite') || t.includes('serrurerie')) return 10;
    return 99;
  };

  useEffect(() => {
    setLoggedInUser(getLoggedInUser());
  }, []);

  useEffect(() => {
    if (!navigator?.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => setUserPosition([position.coords.latitude, position.coords.longitude]),
      () => setUserPosition(null)
    );
  }, []);

  useEffect(() => {
    let filtered = [...services];
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(service =>
        service.title?.toLowerCase().includes(query) ||
        service.description?.toLowerCase().includes(query) ||
        (Array.isArray(service.tags) ? service.tags.some(tag => tag.toLowerCase().includes(query)) : false)
      );
    }
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(service => {
        const tags = Array.isArray(service.tags) ? service.tags : [service.tags];
        return tags.some(tag => tag === selectedCategory);
      });
    }
    if (selectedGroup !== 'all') {
      const groupTags = serviceGroups[selectedGroup] || [];
      filtered = filtered.filter(service => {
        const tags = Array.isArray(service.tags) ? service.tags : [service.tags];
        return tags.some(tag => groupTags.includes(tag));
      });
    }
    if (sortBy === 'name-asc') {
      filtered.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    } else if (sortBy === 'name-desc') {
      filtered.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
    } else if (sortBy === 'newest') {
      filtered.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    } else {
      filtered.sort((a, b) => {
        const priorityDiff = getServicePriority(a) - getServicePriority(b);
        if (priorityDiff !== 0) return priorityDiff;
        return (a.title || '').localeCompare(b.title || '');
      });
    }
    setFilteredServices(filtered);
  }, [services, searchQuery, selectedCategory, selectedGroup, sortBy]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        try {
          await api.initializeServices();
        } catch (e) {
          /* ignore */
        }
        await api.initializeServices(false);
        const response = await api.getServices({});
        if (response.success) {
          const servicesWithFixedPaths = (response.data || []).map(service => ({
            ...service,
            cover: service.cover?.startsWith('/') ? service.cover : `/${service.cover}`,
            user: service.user ? {
              ...service.user,
              cover: service.user.cover?.startsWith('/') ? service.user.cover : `/${service.user.cover}`
            } : service.user
          }));
          setServices(servicesWithFixedPaths);
        }
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const featuredServices = filteredServices.slice(0, 2);
  const otherServices = filteredServices.slice(2);
  const providerPosition = userPosition
    ? [userPosition[0] + 0.01, userPosition[1] + 0.012]
    : [36.8165, 10.1915];
  const mapCenter = userPosition || [36.8065, 10.1815];
  const isMechanicService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('mecancien') || t.includes('mécancien') || t.includes('mecanicien') || t.includes('mécanicien');
  
  };
  const isSecurityService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('sécurité') || t.includes('securite') || t.includes('serrurerie');
  };
  const isMovingService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('déménagement') || t.includes('demenagement') || t.includes('transport');
  };
  const isCleaningService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('ménage') || t.includes('menage') || t.includes('nettoyage') || t.includes('netoyage');
  };
  const isGardeningService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('jardinage') || t.includes('jardin') || t.includes('extérieur') || t.includes('exterieur');
  };
  const isCarpentryService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('menuiserie') || t.includes('bois');
  };
  const isElectricityService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('électricité') || t.includes('electricite') || t.includes('électrique') || t.includes('electrique');
  };
  const isPlumbingService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('plomberie') || t.includes('chauffage');
  };
  const isFloorWallService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('sol') || t.includes('mur');
  };
  const isBathroomService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('salle de bain') || t.includes('salledebain') || t.includes('bain');
  };
  const isKitchenService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('cuisine');
  };
  const isFurnitureDecorationService = (service) => {
    const t = String(service?.title || '').toLowerCase();
    return t.includes('meuble') || t.includes('décoration') || t.includes('decoration');
  };
  const getServiceDestination = (service, serviceId) => {
    if (isMechanicService(service)) return '/services/mecancien';
    if (isSecurityService(service)) return '/services/securite-serrurerie';
    if (isMovingService(service)) return '/services/demenagement';
    if (isCleaningService(service)) return '/services/menage-nettoyage';
    if (isGardeningService(service)) return '/services/jardinage';
    if (isCarpentryService(service)) return '/services/menuiserie';
    if (isElectricityService(service)) return '/services/electricite';
    if (isPlumbingService(service)) return '/services/plomberie-chauffage';
    if (isFloorWallService(service)) return '/services/sol-mur';
    if (isBathroomService(service)) return '/services/salle-de-bain';
    if (isKitchenService(service)) return '/services/cuisine';
    if (isFurnitureDecorationService(service)) return '/services/meuble-decoration';
    return `/service/${serviceId}/providers`;
  };

  const getServiceCover = (service) => {
    if (isMechanicService(service)) return '/images/mecanique-aa.png';
    if (isKitchenService(service)) return '/images/servicecuisine.jpg';
    const cover = service.cover || '/images/blog/a.jpg';
    return cover.startsWith('/') ? cover : `/${cover}`;
  };
  const getServiceDisplayTitle = (service) => {
    if (service?.titles?.[language]) return service.titles[language];
    if (isMovingService(service)) return translateServiceLabel(t, 'Déménagement');
    return translateServiceLabel(t, service?.title);
  };

  const renderServiceCard = (service, idx, { featured = false, featuredCount = 0 } = {}) => {
    const serviceId = service._id || encodeURIComponent(service.title);
    const isMyService = isProviderForService(loggedInUser, service);
    const to = getServiceDestination(service, serviceId);
    const coverUrl = getServiceCover(service);
    const isPromoted = isFloorWallService(service)
      || isFurnitureDecorationService(service)
      || isSecurityService(service)
      || isMechanicService(service);
    const colProps = isPromoted ? { xs: 12, md: 6, lg: 6 } : { xs: 6, md: 6, lg: 3 };

    return (
      <Col {...colProps} key={service._id || `${featured ? 'f' : 'o'}-${idx}`}>
        <Link
          to={to}
          className={`home-service-tile ${isPromoted ? 'home-service-tile--large' : ''} ${isMyService ? 'border border-info border-2' : ''}`}
        >
          <div className="home-service-tile__bg" style={{ backgroundImage: `url(${coverUrl})` }} />
          <div className="home-service-tile__overlay" aria-hidden />
          {isMyService && (
            <NioBadge className={`home-service-tile__badge ${isPromoted ? 'm-3' : 'm-2'} text-bg-info`}>
              {t('common.yourService')}
            </NioBadge>
          )}
          <span className="home-service-tile__title">{getServiceDisplayTitle(service)}</span>
        </Link>
      </Col>
    );
  };
  return (
    <AppLayout variant={11} title={t('home.servicesTitle')} rootClass="layout-10" >
      <style>{`
        .home-services-filter-wrap {
          background: #f8fbff;
          border: 1px solid #e9eff6;
          border-radius: 14px;
          padding: 1.25rem;
        }
        @media (min-width: 768px) {
          .home-services-filter-wrap {
            padding: 1.5rem;
          }
        }
        .home-service-tile {
          position: relative;
          display: block;
          overflow: hidden;
          border-radius: 14px;
          min-height: 150px;
          box-shadow: 0 6px 18px rgba(15, 23, 42, 0.12);
          transition: box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease;
        }
        .home-service-tile:hover {
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.18);
          transform: translateY(-2px);
        }
        .home-service-tile--large {
          min-height: 180px;
        }
        @media (min-width: 992px) {
          .home-service-tile--large {
            min-height: 210px;
          }
        }
        .home-service-tile__bg {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          transition: transform 0.45s ease;
        }
        .home-service-tile:hover .home-service-tile__bg {
          transform: scale(1.06);
        }
        .home-service-tile__overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            180deg,
            rgba(15, 23, 42, 0.1) 0%,
            rgba(15, 23, 42, 0.3) 100%
          );
          pointer-events: none;
        }
        .home-service-tile__badge {
          position: absolute;
          top: 0;
          right: 0;
          z-index: 2;
        }
        .home-service-tile__title {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem 1.25rem;
          margin: 0;
          color: #fff;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          text-align: center;
          font-size: clamp(0.95rem, 1.3vw, 1.15rem);
          line-height: 1.3;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.65);
          z-index: 1;
        }
        .home-service-tile--large .home-service-tile__title {
          font-size: clamp(1.15rem, 2vw, 1.45rem);
          letter-spacing: 0.03em;
        }
        .nk-banner-bs-management,
        .nk-banner-bs-management .nk-banner-wrap {
          background: #001f3f !important;
          background-image: none !important;
        }
        .nk-banner-bs-management::before,
        .nk-banner-bs-management::after,
        .nk-banner-bs-management .nk-banner-wrap::before,
        .nk-banner-bs-management .nk-banner-wrap::after {
          background: #001f3f !important;
          background-image: none !important;
          border-color: #001f3f !important;
        }
        .home-steps-row {
          row-gap: 1.25rem;
        }
        @media (min-width: 992px) {
          .home-steps-row {
            column-gap: 0;
            flex-wrap: nowrap;
            justify-content: center !important;
          }
        }
        .home-step-title {
          font-size: 1.15rem;
          line-height: 1.35;
          white-space: normal !important;
          font-weight: 600;
        }
        .home-step-text {
          font-size: 0.82rem !important;
          line-height: 1.45;
          max-width: 240px;
          margin: 0 auto;
        }
        .home-step-icon {
          font-size: 1.55rem !important;
          margin-bottom: 0.6rem !important;
        }
        .home-step-image {
          width: 28px;
          height: 28px;
          object-fit: contain;
          margin-bottom: 0.6rem;
          display: block;
        }
        .home-step-card {
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
        }
        .home-process-section__title {
          color: #12314f;
          font-weight: 700;
          text-align: center;
          margin-bottom: 2rem;
        }
        .home-process-step__icon {
          width: 120px;
          height: 70px;
          object-fit: contain;
          display: block;
          margin: 0 auto 1rem;
        }
        .home-process-step__title {
          color: #12314f;
          font-size: 1.45rem;
          font-weight: 700;
          margin-bottom: 0.6rem;
        }
        .home-process-step__text {
          color: #475569;
          line-height: 1.6;
          margin-bottom: 0;
        }
        .home-process-reviews {
          margin-top: 2.5rem;
          border-top: 1px solid #e5e7eb;
          padding-top: 1.5rem;
        }
        .home-process-review {
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          padding: 0.9rem;
          height: 100%;
        }
        .home-process-review__stars {
          color: #10b981;
          font-weight: 700;
          font-size: 0.92rem;
        }
        .home-process-review__text {
          color: #334155;
          font-size: 0.92rem;
          margin: 0.5rem 0 0;
          line-height: 1.45;
        }
        .home-map-card {
          margin-top: 1.4rem;
          border: 1px solid #d7e3f7;
          border-radius: 16px;
          background: #fff;
          overflow: hidden;
          box-shadow: 0 10px 28px rgba(15, 23, 42, 0.08);
        }
        .home-map-card__head {
          padding: 0.9rem 1rem;
          border-bottom: 1px solid #e6eefc;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 0.7rem;
        }
        .home-map-card__title {
          margin: 0;
          color: #0f2b46;
          font-size: 0.95rem;
          font-weight: 700;
        }
        .home-map-card__legend {
          display: flex;
          align-items: center;
          gap: 0.8rem;
          color: #5f6b7a;
          font-size: 0.82rem;
        }
        .home-map-card__legend-dot {
          width: 9px;
          height: 9px;
          border-radius: 999px;
          display: inline-block;
          margin-right: 0.35rem;
        }
        .home-map-card__legend-dot--user {
          background: #2563eb;
        }
        .home-map-card__legend-dot--provider {
          background: #dc2626;
        }
        .home-map-card__body {
          min-height: 280px;
        }
        .home-map-card__body .leaflet-container {
          width: 100%;
          height: 280px;
        }
        .home-map-marker {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .home-map-marker span {
          width: 14px;
          height: 14px;
          border-radius: 999px;
          border: 2px solid #fff;
          box-shadow: 0 4px 10px rgba(15, 23, 42, 0.2);
        }
        .home-map-marker--user span {
          background: #2563eb;
        }
        .home-map-marker--provider span {
          background: #dc2626;
        }
      `}</style>

      {/*  Banner Section Start   */}
      <section
        className="nk-banner nk-banner-bs-management is-theme"
        style={{ background: '#001f3f' }}
      >
        <div className="nk-banner-wrap">
          <Container>
            <Row className="justify-content-center">
              <Col lg={8} xl={6}>
                <div className="nk-banner-content text-center">
                  <div>
                    <h1 className="text-capitalize mb-2">{t('home.bannerTitle')}</h1>
                    <p className="fs-20">{t('home.bannerSub')}</p>
                  </div>
                
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </section>
      {/*  Banner Section End   */} 
      {/*  Section Services (contenu page Services)   */}
      <NioSection className="pb-7 pb-lg-120 overflow-hidden" masks={["blur-1 left center"]}>
        <NioSection.Content>
          <Row className="justify-content-center text-center">
            <Col lg={8} xl={6}>
              <div className="nk-section-head">
                <br></br> <br></br> 
                <h2>{t('home.servicesTitle')}</h2>
                <p className="fs-20">
                  {t('home.servicesSub')}
                </p>
                
              </div>
            </Col>
            <Col lg={10} xl={12}>
              <div className="nk-filter-wrap pb-5 pb-md-7 home-services-filter-wrap">
                <div className="mb-4">
                  <NioField.Input
                    icon="search before z-1"
                    placeholder={t('home.searchPlaceholder')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <Row className="g-3 mb-4">
                  <Col md={6} lg={4}>
                    <div className="form-group">
                      <label className="form-label fw-semibold mb-2">{t('home.groupLabel')}</label>
                      <select
                        className="form-control form-control-lg"
                        value={selectedGroup}
                        onChange={(e) => setSelectedGroup(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      >
                        {Object.keys(serviceGroups).map(group => (
                          <option key={group} value={group}>{t(serviceGroupLabels[group] || group)}</option>
                        ))}
                      </select>
                    </div>
                  </Col>
                  <Col md={6} lg={4}>
                    <div className="form-group">
                      <label className="form-label fw-semibold mb-2">{t('home.sortLabel')}</label>
                      <select
                        className="form-control form-control-lg"
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        style={{ cursor: 'pointer' }}
                      >
                        <option value="default">{t('home.sortDefault')}</option>
                        <option value="name-asc">{t('home.sortNameAsc')}</option>
                        <option value="name-desc">{t('home.sortNameDesc')}</option>
                        <option value="newest">{t('home.sortNewest')}</option>
                      </select>
                    </div>
                  </Col>
                  <Col md={12} lg={4}>
                    <div className="form-group">
                      <label className="form-label fw-semibold mb-2">{t('home.results', { count: filteredServices.length })}</label>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-info"
                        onClick={() => { setSearchQuery(''); setSelectedCategory('all'); setSelectedGroup('all'); setSortBy('default'); }}
                      >
                        {t('home.clearFilters')}
                      </button>
                    </div>
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
          <Row>
            <Col xl={12}>
              {loading ? (
                <div className="text-center py-5">
                  <p className="fs-18">{t('home.loadingServices')}</p>
                </div>
              ) : filteredServices.length > 0 ? (
                <>
                  {featuredServices.length > 0 && (
                    <Row className="g-2 g-lg-3 mb-2 mb-lg-3">
                      {featuredServices.map((service, idx) => renderServiceCard(service, idx, { featured: true, featuredCount: featuredServices.length }))}
                    </Row>
                  )}
                  {otherServices.length > 0 && (
                    <Row className="g-2 g-lg-3">
                      {otherServices.map((service, idx) => renderServiceCard(service, idx))}
                    </Row>
                  )}
                  <div className="home-map-card">
                    <div className="home-map-card__head">
                      <h4 className="home-map-card__title">{t('home.mapTitle')}</h4>
                      <div className="home-map-card__legend">
                        <span><span className="home-map-card__legend-dot home-map-card__legend-dot--user" />{t('home.mapUser')}</span>
                        <span><span className="home-map-card__legend-dot home-map-card__legend-dot--provider" />{t('home.mapProvider')}</span>
                      </div>
                    </div>
                    <div className="home-map-card__body">
                      <MapContainer center={mapCenter} zoom={12} scrollWheelZoom={false}>
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <Marker position={mapCenter} icon={userMarkerIcon}>
                          <Popup>{userPosition ? t('home.mapYourPosition') : t('home.mapUser')}</Popup>
                        </Marker>
                        <Marker position={providerPosition} icon={providerMarkerIcon}>
                          <Popup>{t('home.mapProvider')}</Popup>
                        </Marker>
                      </MapContainer>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-5">
                  <p className="fs-18">{t('home.noServices')}</p>
                </div>
              )}
            </Col>
          </Row>
          <div className="text-center pt-5 pt-lg-7">
            <Link to="/services"><NioButton label={t('home.viewAllServices')} className="btn-info-soft btn-lg px-5" /></Link>
          </div>
        </NioSection.Content>
      </NioSection>
      {/*  Section Services End   */}
      {/*  Works Section Start   */}
      <NioSection className="bg-white border-top border-light">
        <NioSection.Content>
            <Row className="justify-content-center text-center mb-5">
            <Col lg={9}>
              <h2 className="h1">{t('home.howTitle')}</h2>
            </Col>
          </Row>
          <Row className="home-steps-row justify-content-center g-4 g-lg-0 text-center">
            <Col sm={6} lg={3}>
              <div className="home-step-card">
                <img
                  src="/images/publiez.svg"
                  alt="Publier une demande"
                  className="home-step-image"
                  onError={(e) => {
                    e.currentTarget.src = '/images/publiez.svg';
                  }}
                />
                <h4 className="home-step-title text-primary mb-2">{t('home.step1Title')}</h4>
                <p className="home-step-text text-muted mb-0">{t('home.step1Text')}</p>
              </div>
            </Col>
            <Col sm={6} lg={3}>
              <div className="home-step-card">
              <img
                src="/images/recevez.svg"
                alt="Recevez un devis sous 24h"
                className="home-step-image"
                onError={(e) => {
                  e.currentTarget.src = '/images/publiez.svg';
                }}
              />
                <h4 className="home-step-title text-primary mb-2">{t('home.step2Title')}</h4>
                <p className="home-step-text text-muted mb-0">{t('home.step2Text')}</p>
              </div>
            </Col>
            <Col sm={6} lg={3}>
              <div className="home-step-card">
              <img
                src="/images/prestataire.svg"
                alt="Choisissez votre prestataire"
                className="home-step-image"
                onError={(e) => {
                  e.currentTarget.src = '/images/publiez.svg';
                }}
              />
                  <h4 className="home-step-title text-primary mb-2">{t('home.step3Title')}</h4>
                <p className="home-step-text text-muted mb-0">{t('home.step3Text')}</p>
              </div>
            </Col>
            <Col sm={6} lg={3}>
              <div className="home-step-card">
              <img
                src="/images/trouvez.svg"
                alt="Trouvez le bon prestataire en quelques clics"
                className="home-step-image"
                onError={(e) => {
                  e.currentTarget.src = '/images/publiez.svg';
                }}
              />
                <h4 className="home-step-title text-primary mb-2">{t('home.step4Title')}</h4>
                <p className="home-step-text text-muted mb-0">{t('home.step4Text')}</p>
              </div>
            </Col>
          </Row>
        </NioSection.Content>
      </NioSection>
      {/*  Works Section End   */}
      <NioSection className="pt-5 pt-lg-7">

        <NioSection.Content>
          <Row className="justify-content-center mb-5">
            <Col lg={11}>
              <h2 className="text-center mb-4">{t('home.trustTitle')}</h2>
              <br/>
              <Row className="align-items-start g-4 g-lg-5">
                <Col lg={6}>
                  <div className="overflow-hidden rounded-3 shadow-sm">
                    <img
                      src="/images/img.jpg"
                      alt="Transformez votre maison avec Allo Alla"
                      className="w-100"
                      style={{ height: 'auto' }}
                    />
                  </div>
                </Col>
                <Col lg={6}>
                  <p className="fs-20 mb-3">
                    <strong>{t('home.trustP1')}</strong>
                  </p>
                  <p className="fs-20 mb-3">
                    <strong>{t('home.trustP2')}</strong>
                  </p>
                  <p className="fs-20 mb-0">
                    {t('home.trustP3')}
                  </p>
                </Col>
              </Row>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col lg={11}>
              <Row className="g-4 g-lg-5">
                <Col lg={6}>
              
                  <p className="fs-20 mb-0">
                    {t('home.trustP4')}
                  </p>
                </Col>
                <Col lg={6}>
                  <p className="fs-20 mb-0">
                    {t('home.trustP5')}
                  </p>
                </Col>
              </Row>
            </Col>
          </Row>

          <Row className="justify-content-center mt-5 mt-lg-7">
            <Col lg={11}>
              <h2 className="text-center mb-4">{t('home.providerTitle')}</h2>
              <br/>
              <Row className="align-items-start g-4 g-lg-5">
                <Col lg={6}>
                  <p className="fs-20 mb-3">
                    {t('home.providerP1')}
                  </p>
                  <p className="fs-20 mb-3">
                    {t('home.providerP2')}
                  </p>
                  <p className="fs-20 mb-3">
                    {t('home.providerP3')}
                  </p>

                  <ul className="list-unstyled mb-3">
                    <li className="d-flex align-items-start gap-2 mb-2">
                      <NioIcon name="check-circle-fill" className="text-primary mt-1" />
                      <strong>{t('home.providerLi1')}</strong>
                    </li>
                    <li className="d-flex align-items-start gap-2 mb-2">
                      <NioIcon name="check-circle-fill" className="text-primary mt-1" />
                      <strong>{t('home.providerLi2')}</strong>
                    </li>
                    <li className="d-flex align-items-start gap-2">
                      <NioIcon name="check-circle-fill" className="text-primary mt-1" />
                      <strong>{t('home.providerLi3')}</strong>
                    </li>
                  </ul>

                  <p className="fs-20 mb-3">
                    {t('home.providerP4')}
                  </p>

                  <Link to="/post-request" className="fw-semibold text-primary">
                    {t('home.becomeArtisan')} <NioIcon name="arrow-right" className="ms-1" />
                  </Link>
                </Col>

                <Col lg={6}>
                  <div className="overflow-hidden rounded-3 shadow-sm">
                    <img
                      src="/images/a.jpg"
                      alt="Devenir artisan sur Allo Alla"
                      className="w-100"
                      style={{ height: 'auto' }}
                    />
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </NioSection.Content>
      </NioSection>
     

    </AppLayout>
  )
}

export default BusinessManagement;